import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import type { PickerChangeImportance } from "@mui/x-date-pickers/models";
import type { PickersShortcutsProps } from "@mui/x-date-pickers/PickersShortcuts";
import { useIsValidValue, usePickerContext } from "@mui/x-date-pickers/hooks";
import { Button, ButtonGroup } from "@mui/material";
import type { ReactNode, Ref } from "react";
import { useMemo, useState } from "react";
import { appToday } from "../lib/devDate";

function parseDateValue(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

function dateInputValue(date: Date | null) {
  if (!date || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dateShortcutValue(offsetDays: number) {
  const date = appToday();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + offsetDays);
  return date;
}

function sameCalendarDate(left: Date | null, right: Date) {
  return (
    Boolean(left) &&
    !Number.isNaN(left!.getTime()) &&
    left!.getFullYear() === right.getFullYear() &&
    left!.getMonth() === right.getMonth() &&
    left!.getDate() === right.getDate()
  );
}

function DateShortcuts({ changeImportance = "accept" as PickerChangeImportance, className }: PickersShortcutsProps<Date | null>) {
  const { disabled, setValue, value } = usePickerContext<Date | null>();
  const isValidValue = useIsValidValue<Date | null>();
  const shortcuts = [
    { id: "today", label: "Today", value: dateShortcutValue(0) },
    { id: "yesterday", label: "Yesterday", value: dateShortcutValue(-1) },
  ];

  return (
    <div className={`date-picker-shortcuts${className ? ` ${className}` : ""}`}>
      <ButtonGroup variant="outlined" aria-label="Date shortcuts">
        {shortcuts.map((shortcut) => {
          const active = sameCalendarDate(value, shortcut.value);
          return (
            <Button
              key={shortcut.id}
              type="button"
              variant={active ? "contained" : "outlined"}
              disabled={disabled || !isValidValue(shortcut.value)}
              onClick={() =>
                setValue(shortcut.value, {
                  changeImportance,
                  shortcut: { id: shortcut.id, label: shortcut.label },
                  source: "view",
                })
              }
            >
              {shortcut.label}
            </Button>
          );
        })}
      </ButtonGroup>
    </div>
  );
}

type DatePickerFieldProps = {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  label?: ReactNode;
  name?: string;
  required?: boolean;
  showShortcuts?: boolean;
  size?: "small" | "medium";
  startAdornment?: ReactNode;
  inputRef?: Ref<HTMLInputElement>;
};

export function DatePickerField({
  value,
  onChange,
  className,
  disabled = false,
  error = false,
  helperText,
  label = "Date",
  name,
  required = false,
  showShortcuts = true,
  size = "medium",
  startAdornment,
  inputRef,
}: DatePickerFieldProps) {
  const [open, setOpen] = useState(false);
  const dateValue = useMemo(() => parseDateValue(value), [value]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {name ? <input type="hidden" name={name} value={value} /> : null}
      <DatePicker
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={dateValue}
        disabled={disabled}
        format="dd.MM.yyyy"
        views={["year", "month", "day"]}
        openTo="day"
        onChange={(nextDate) => onChange(dateInputValue(nextDate))}
        slots={showShortcuts ? { shortcuts: DateShortcuts } : undefined}
        slotProps={{
          layout: {
            sx: {
              gridAutoColumns: "minmax(0, 1fr)",
              "& .MuiPickersLayout-shortcuts": {
                gridColumn: "1 / 4",
                gridRow: 1,
              },
              "& .MuiPickersLayout-contentWrapper": {
                gridColumn: "1 / 4",
                gridRow: 2,
              },
              "& .MuiPickersLayout-actionBar": {
                gridColumn: "1 / 4",
                gridRow: 3,
              },
            },
          },
          shortcuts: showShortcuts ? { changeImportance: "accept" } : undefined,
          textField: {
            inputRef,
            className: `date-picker-field${className ? ` ${className}` : ""}`,
            error,
            helperText,
            label,
            required,
            size,
            onClick: () => {
              if (!disabled) setOpen(true);
            },
            slotProps: {
              input: {
                readOnly: true,
                startAdornment,
              },
              inputLabel: {
                shrink: true,
              },
            },
          },
          openPickerButton: {
            className: "date-picker-button",
            title: "Open calendar",
          },
        }}
      />
    </LocalizationProvider>
  );
}
